import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { buildInviteDecisionWhatsAppMessage } from "../lib/schoolFeeMessages";
import ProfileThumbnail from "./ProfileThumbnail";
import { Button, Card, Badge } from "./ui";
import WhatsAppFollowUpPrompt from "./WhatsAppFollowUpPrompt";

export default function HostInviteRequests({
  requests, eventId, isPaid, tokenParam, onUpdated,
  demoMode = false, onDemoInviteAction,
  eventName = "", hostName = "", schoolName = "",
}) {
  const [acting, setActing] = useState(null);
  const [followUp, setFollowUp] = useState(null);

  async function handleAction(request, action) {
    setActing(request.id);
    try {
      if (demoMode) {
        onDemoInviteAction?.(request.id, action);
        toast.success(action === "accept" ? "Guest added to the dance floor!" : "Invite request declined.");
      } else {
        await api.post(`/events/${eventId}/invite-requests/${request.id}/${action}`, null, {
          params: { token: tokenParam || undefined },
        });
        toast.success(action === "accept" ? "Guest added to the dance floor!" : "Invite request declined.");
        onUpdated();
      }
      const accepted = action === "accept";
      setFollowUp({
        phone: request.phone,
        accepted,
        name: request.name,
        message: buildInviteDecisionWhatsAppMessage({
          guestName: request.name,
          eventName,
          accepted,
          hostName,
          schoolName,
        }),
      });
    } catch {
      toast.error("Couldn't update — try again?");
    } finally {
      setActing(null);
    }
  }

  return (
    <>
      <Card className="p-5">
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="font-ui font-medium text-[12px] text-ink/50 tracking-wide">Invite requests</p>
            <h2 className="font-ui font-semibold text-[20px] sm:text-[22px] leading-tight mt-0.5">
              {requests.length} waiting for approval
            </h2>
          </div>
          {isPaid && (
            <Badge color="amber">Confirm after payment</Badge>
          )}
        </div>
        {requests.length === 0 ? (
          <p className="font-normal text-[14px] text-ink/55 p-3 text-pretty">
            No pending requests yet. Share the link — guests will appear here when they request an invite.
          </p>
        ) : (
          <div className="space-y-2" data-testid="invite-request-list">
            {requests.map((r) => (
              <div
                key={r.id}
                className="rounded-card border border-black/[0.06] bg-white p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`invite-request-${r.id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <ProfileThumbnail
                    name={r.name}
                    role={r.role}
                    thumbnailUrl={r.thumbnail_url}
                    size="sm"
                  />
                  <div className="min-w-0">
                    <p className="font-ui font-semibold text-[15px] truncate">{r.name}</p>
                    <p className="text-[13px] font-medium text-ink/60">
                      {r.role}
                      {r.first_timer ? " · First timer" : ""}
                      {r.hide_name ? " · Hidden name" : ""}
                    </p>
                    {r.message && (
                      <p className="text-[13px] font-normal text-ink/70 mt-1 italic">&ldquo;{r.message}&rdquo;</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="primary"
                    onClick={() => handleAction(r, "accept")}
                    disabled={acting === r.id}
                    data-testid={`accept-invite-${r.id}`}
                  >
                    Accept
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleAction(r, "reject")}
                    disabled={acting === r.id}
                    data-testid={`reject-invite-${r.id}`}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      {followUp ? (
        <WhatsAppFollowUpPrompt
          title={followUp.accepted ? "Invite accepted" : "Invite declined"}
          description={`Send ${followUp.name || "the guest"} a quick WhatsApp note about your decision.`}
          phone={followUp.phone}
          message={followUp.message}
          onClose={() => setFollowUp(null)}
          testId="invite-decision-whatsapp"
        />
      ) : null}
    </>
  );
}
